import { useState, useEffect } from 'react';
import { X, Check, Loader2, Layout, Copy, FileText } from 'lucide-react';
import * as AssessmentBuilderAPI from '../../services/assessmentBuilderApi';
import TemplatePreview from './ScreenBuilder/TemplatePreview';
import { QUESTION_TYPE_DISPLAY, displayQuestionType as displayType } from './ScreenBuilder/constants';


/**
 * First Question Modal
 * Shows when user adds the first question of a type to an assessment
 * Offers template options: System Default, Copy from Another Assessment, or Blank
 */
const FirstQuestionModal = ({ 
  show, 
  onClose, 
  questionType, 
  assessmentId, 
  onTemplateSelected,
  onPreferenceChange, // New prop to handle "don't show again"
  questionData // Actual question data to show in preview
}) => {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState({
    system_templates: [],
    sponsor_templates: [],
    assessment_templates_from_other_assessments: []
  });
  const [selectedOption, setSelectedOption] = useState('system'); // 'system', 'copy', 'blank'
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [setAsDefault, setSetAsDefault] = useState(true);

  // Load available templates when modal opens
  useEffect(() => {
    if (show && questionType && assessmentId) {
      loadTemplates();
    }
  }, [show, questionType, assessmentId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await AssessmentBuilderAPI.getAvailableTemplates(questionType, assessmentId);
      setTemplates(data);
      
      // Auto-select first system template if available
      if (data.system_templates.length > 0) {
        setSelectedTemplate(data.system_templates[0]);
        setSelectedOption('system');
      } else if (data.assessment_templates_from_other_assessments.length > 0) {
        setSelectedOption('copy');
        setSelectedAssessment(data.assessment_templates_from_other_assessments[0]);
        setSelectedTemplate(data.assessment_templates_from_other_assessments[0].template);
      } else {
        setSelectedOption('blank');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    console.log('[TEMPLATE] Modal handleApply called');
    console.log('[TEMPLATE] Modal state:', {
      selectedOption,
      hasTemplate: !!selectedTemplate,
      templateName: selectedTemplate?.name,
      setAsDefault
    });
    
    let templateToApply = null;

    if (selectedOption === 'system' && selectedTemplate) {
      templateToApply = selectedTemplate;
    } else if (selectedOption === 'copy' && selectedTemplate) {
      templateToApply = selectedTemplate;
    }

    console.log('[TEMPLATE] Calling onTemplateSelected...');

    // Call parent callback
    onTemplateSelected({
      option: selectedOption,
      template: templateToApply,
      setAsDefault: setAsDefault
    });
    
    console.log('[TEMPLATE] ✓ onTemplateSelected called');
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            First {displayType(questionType)} Question
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin mr-3" />
              <span className="text-gray-600 dark:text-gray-300">Loading template options...</span>
            </div>
          ) : (
            <>
              <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
                <p className="text-sm text-gray-800 dark:text-gray-100 mb-2">
                  <strong>First {displayType(questionType)} Question</strong>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Choose how to layout <strong>{displayType(questionType)}</strong> questions in this assessment. 
                  You can use a pre-designed template or create a custom layout.
                </p>
              </div>

              <div className="space-y-3">
                {/* No templates available message */}
                {templates.system_templates.length === 0 && 
                 templates.assessment_templates_from_other_assessments.length === 0 && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-3">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      ℹ️ No pre-designed templates available for <strong>{displayType(questionType)}</strong> questions. 
                      You'll start with a blank layout and can configure it manually.
                    </p>
                  </div>
                )}
                
                {/* Option 1: Use System Default */}
                {templates.system_templates.length > 0 && (
                  <div
                    onClick={() => {
                      setSelectedOption('system');
                      setSelectedTemplate(templates.system_templates[0]);
                    }}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedOption === 'system'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 mr-3 flex items-center justify-center ${
                        selectedOption === 'system'
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selectedOption === 'system' && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Layout className="w-4 h-4 text-primary-600 dark:text-primary-400 mr-2" />
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Use System Default</h3>
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 rounded">
                            Recommended
                          </span>
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-200 mb-1">
                          {templates.system_templates[0].name}
                        </p>
                        {templates.system_templates[0].description && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {templates.system_templates[0].description}
                          </p>
                        )}
                        {selectedOption === 'system' && (
                          <div className="mt-3">
                            <TemplatePreview template={templates.system_templates[0]} questionData={questionData} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Option 2: Copy from Another Assessment */}
                {templates.assessment_templates_from_other_assessments.length > 0 && (
                  <div
                    onClick={() => setSelectedOption('copy')}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedOption === 'copy'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 mr-3 flex items-center justify-center ${
                        selectedOption === 'copy'
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selectedOption === 'copy' && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Copy className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Copy from Another Assessment</h3>
                        </div>
                        <select
                          value={selectedAssessment?.assessment_id || ''}
                          onChange={(e) => {
                            const selected = templates.assessment_templates_from_other_assessments.find(
                              a => a.assessment_id === parseInt(e.target.value)
                            );
                            if (selected) {
                              setSelectedAssessment(selected);
                              setSelectedTemplate(selected.template);
                              setSelectedOption('copy');
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500"
                        >
                          {templates.assessment_templates_from_other_assessments.map(item => (
                            <option key={item.assessment_id} value={item.assessment_id}>
                              {item.assessment_name}
                            </option>
                          ))}
                        </select>
                        {selectedAssessment && selectedOption === 'copy' && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              Template: {selectedAssessment.template.name}
                            </p>
                            <TemplatePreview template={selectedAssessment.template} questionData={questionData} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Option 3: Start with Blank Layout */}
                <div
                  onClick={() => {
                    setSelectedOption('blank');
                    setSelectedTemplate(null);
                  }}
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    selectedOption === 'blank'
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 mr-3 flex items-center justify-center ${
                      selectedOption === 'blank'
                        ? 'border-primary-600 bg-primary-600'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}>
                      {selectedOption === 'blank' && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <FileText className="w-4 h-4 text-gray-600 dark:text-gray-400 mr-2" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Start with Blank Layout</h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        I'll configure the layout manually
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Set as Default Checkbox */}
              {selectedOption !== 'blank' && (
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={setAsDefault}
                      onChange={(e) => setSetAsDefault(e.target.checked)}
                      className="mt-0.5 mr-3 rounded border-gray-300 dark:border-gray-600"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Use this layout for all {displayType(questionType)} questions in this assessment
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">
                        Future {displayType(questionType)} questions will automatically use this layout
                      </p>
                    </div>
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          {/* Preference Toggle */}
          <label className="flex items-center text-sm text-gray-600 dark:text-gray-300 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="mr-2 rounded border-gray-300 dark:border-gray-600"
            />
            <span>Don't show this dialog for new questions (you can still manually apply templates)</span>
          </label>
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                if (dontShowAgain && onPreferenceChange) {
                  onPreferenceChange(false); // Disable auto-show
                }
                onClose();
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Skip
            </button>
            <button
              onClick={() => {
                if (dontShowAgain && onPreferenceChange) {
                  onPreferenceChange(false); // Disable auto-show
                }
                handleApply();
              }}
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center ${
                loading
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              <Check className="w-4 h-4 mr-2" />
              Apply & Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirstQuestionModal;
